import React, { useState } from 'react';
import Modal from '../Modal/Modal';
import DropdownSelector from '../DropdownSelector/DropdownSelector';
import SearchBar from '../SearchBar/SearchBar';
import GenericTooltip from '../GenericTooltip/GenericTooltip';
import { useConstants } from '../../constant/constantProvider';
import './TalentPulseFilterBar.css';

const TalentPulseFilterBar = ({
    type = 'all',
    onToggleType = () => { },
    profileTypes = [
        { id: 'all', label: 'All' },
        { id: 'external', label: 'External' }
    ],
    roles = [],
    selectedRole = null,
    onRoleChange = () => { },
    companies = [],
    selectedCompany = null,
    onCompanyChange = () => { },
    regions = [],
    selectedRegion = null,
    onRegionChange = () => { },
    states = [],
    selectedState = null,
    onStateChange = () => { },
    territories = [],
    allTerritories = [],
    territoriesByState = {},
    territoryDisabled = false,
    selectedTerritory = null,
    onTerritoryChange = () => { },
    sort = 'latest',
    onSortChange = () => { },
    onCompareClick = () => { },
    compareDisabled = false,
    compareCount = 0,
    isProfilesHidden = false,
    onToggleProfilesClick = () => { },
    isGridView = false,
    onToggleGridView = () => { },
    candidateCount = 0,
    onClearFilters = () => { },
    // Candidates header props
    isCandidateProfilesSelected = false,
    profileSearchQuery = '',
    onProfileSearchChange = () => { },
    onProfileSearchClear = () => { },
    totalProfilesCount = null,
    searchLower = '',
    visibleCandidatesCount = 0,
    isProfilesLoading = false,
    filtersDisabled = false,
    isCompareActive = false,
    onResetClick = () => { },
}) => {
    const constants = useConstants();
    const isControlDisabled = isProfilesLoading || filtersDisabled;
    const [showMobileFilterModal, setShowMobileFilterModal] = useState(false);

    // Draft states for mobile filter modal (only applied on "Apply" click)
    const [draftType, setDraftType] = useState(type);
    const [draftRole, setDraftRole] = useState(selectedRole);
    const [draftCompany, setDraftCompany] = useState(selectedCompany);
    const [draftRegion, setDraftRegion] = useState(selectedRegion);
    const [draftState, setDraftState] = useState(selectedState);
    const [draftTerritory, setDraftTerritory] = useState(selectedTerritory);
    const [draftSort, setDraftSort] = useState(sort);

    const getProfileTypeLabel = (pt) => {
        if (pt.id === 'all') return constants.ALL || pt.label;
        if (pt.id === 'external') return constants.EXTERNAL || pt.label;
        return pt.label;
    };

    const toggleAll = (options, selectedValues, setSelected) => {
        const current = Array.isArray(selectedValues) ? selectedValues : [];
        const allSelected = options.length > 0 && options.every((option) => current.includes(option));
        setSelected(allSelected ? [] : [...options]);
    };
    const getTerritoriesForStates = (stateSelection) => stateSelection?.length
        ? [...new Set(stateSelection.flatMap((state) => territoriesByState[state] || []))]
        : allTerritories;
    const updateDraftStates = (nextStates) => {
        const deselectedStates = (draftState || []).filter((state) => !(nextStates || []).includes(state));
        const deselectedStateTerritories = new Set(
            deselectedStates.flatMap((state) => territoriesByState[state] || [])
        );
        setDraftState(nextStates);
        const validTerritories = new Set(getTerritoriesForStates(nextStates));
        setDraftTerritory((current) => (current || []).filter((territory) => (
            validTerritories.has(territory) && !deselectedStateTerritories.has(territory)
        )));
    };
    const toggleDraftState = (state) => {
        const current = Array.isArray(draftState) ? draftState : [];
        updateDraftStates(current.includes(state) ? current.filter((value) => value !== state) : [...current, state]);
    };
    const toggleAllDraftStates = () => {
        const current = Array.isArray(draftState) ? draftState : [];
        updateDraftStates(states.length > 0 && states.every((state) => current.includes(state)) ? [] : [...states]);
    };
    const draftTerritories = getTerritoriesForStates(draftState);

    const handleOpenMobileModal = () => {
        if (isProfilesHidden && onToggleProfilesClick) {
            onToggleProfilesClick();
        }
        setDraftType(type);
        setDraftRole(selectedRole);
        setDraftCompany(selectedCompany);
        setDraftRegion(selectedRegion);
        setDraftState(selectedState);
        setDraftTerritory(selectedTerritory);
        setDraftSort(sort);
        setShowMobileFilterModal(true);
    };

    const handleApplyMobileFilters = () => {
        if (isProfilesHidden && onToggleProfilesClick) {
            onToggleProfilesClick();
        }
        onToggleType(draftType);
        onRoleChange(draftRole);
        onCompanyChange(draftCompany);
        onRegionChange(draftRegion);
        onStateChange(draftState);
        onTerritoryChange(draftTerritory);
        onSortChange(draftSort);
        setShowMobileFilterModal(false);
    };

    // Helper to map strings to {id, name} for DropdownSelector
    const mapToItems = (arr) => arr.map(item => ({ id: item, name: item }));

    // Count active filter selections
    const activeFiltersCount = [
        selectedRole,
        selectedCompany,
        selectedRegion,
        selectedState,
        selectedTerritory,
    ].filter(Boolean).length;

    const compareDisabledHint = constants.COMPARE_DISABLED_HINT || "Select 2 or more candidates to compare";

    return (
        <>
            <div className="tpf-sticky-block">
                {/* Row 1: Filter bar */}
                <div className="talent-pulse-filter-bar">
                    {/* Desktop Left Dropdowns */}
                    <div
                        className={`psf-left desktop-only-filters${isControlDisabled ? ' tpf-is-loading' : ''}`}
                        onClickCapture={() => {
                            if (isProfilesHidden && onToggleProfilesClick) {
                                onToggleProfilesClick();
                            }
                        }}
                    >
                        {profileTypes && profileTypes.length > 0 && (
                            <div className="seg">
                                {profileTypes.map(pt => (
                                    <button
                                        key={pt.id}
                                        className={type === pt.id ? 'on' : ''}
                                        onClick={() => onToggleType(pt.id)}
                                        disabled={isControlDisabled}
                                    >
                                        {getProfileTypeLabel(pt)} {pt.count !== undefined && `(${pt.count})`}
                                    </button>
                                ))}
                            </div>
                        )}

                        <DropdownSelector
                            placeholder={{ name: constants.ALL_ROLES || "All roles" }}
                            items={mapToItems(roles)}
                            selected={selectedRole}
                            onChange={onRoleChange}
                            type="multi"
                            showTriggerIcon={false}
                            searchable
                            isDisabled={isControlDisabled}
                        />

                        <DropdownSelector
                            placeholder={{ name: constants.ALL_COMPANIES || "All companies" }}
                            items={mapToItems(companies)}
                            selected={selectedCompany}
                            onChange={onCompanyChange}
                            type="multi"
                            showTriggerIcon={false}
                            searchable
                            isDisabled={isControlDisabled}
                        />

                        {/* <DropdownSelector
                            placeholder={{ name: constants.ALL_REGIONS || "All regions" }}
                            items={mapToItems(regions)}
                            selected={selectedRegion}
                            onChange={onRegionChange}
                            showTriggerIcon={false}
                            type="selected-cross"
                            searchable
                            disabled={isControlDisabled}
                        /> */}

                        <DropdownSelector
                            placeholder={{ name: constants.ALL_STATES || "All states" }}
                            items={mapToItems(states)}
                            selected={selectedState}
                            onChange={onStateChange}
                            type="multi"
                            showTriggerIcon={false}
                            searchable
                            isDisabled={isControlDisabled}
                        />

                        <DropdownSelector
                            placeholder={{ name: constants.ALL_TERRITORIES || "All territories" }}
                            items={mapToItems(territories)}
                            selected={selectedTerritory}
                            onChange={onTerritoryChange}
                            type="multi"
                            showTriggerIcon={false}
                            searchable
                            isDisabled={isControlDisabled || territoryDisabled}
                        />

                        <DropdownSelector
                            placeholder={{ name: constants.SORT_LATEST || "Sort: Latest" }}
                            items={[
                                { id: 'latest', name: constants.SORT_LATEST || 'Sort: Latest' },
                                { id: 'az', name: constants.SORT_AZ || 'Sort: A–Z' },
                                { id: 'za', name: 'Sort: Z–A' }
                            ]}
                            selected={sort}
                            onChange={onSortChange}
                            showTriggerIcon={false}
                            itemConfig={{ getValue: (item) => item.id }}
                            isDisabled={isControlDisabled}
                        />
                    </div>

                    {/* Mobile Top Controls Row */}
                    <div className="tpf-mobile-header-row mobile-only-filters">
                        <div className="tpf-mobile-top-bar">
                            {profileTypes && profileTypes.length > 0 && (
                                <div className="seg">
                                    {profileTypes.map(pt => (
                                        <button
                                            key={pt.id}
                                            className={type === pt.id ? 'on' : ''}
                                            onClick={() => onToggleType(pt.id)}
                                            disabled={isControlDisabled}
                                        >
                                            {getProfileTypeLabel(pt)}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <button
                                type="button"
                                className={`btn ghost tpf-filters-trigger ${activeFiltersCount > 0 ? 'has-active' : ''}`}
                                onClick={handleOpenMobileModal}
                                disabled={isControlDisabled}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                                </svg>
                                {constants.FILTERS || "Filters"} {activeFiltersCount > 0 && <span className="tpf-badge">{activeFiltersCount}</span>}
                            </button>

                            <GenericTooltip
                                tooltipText={compareDisabled ? compareDisabledHint : ""}
                                tooltipPosition="bottom"
                                className="tpf-compare-tooltip-wrapper"
                            >
                                <button
                                    type="button"
                                    className="btn tpf-compare-btn"
                                    disabled={compareDisabled}
                                    onClick={onCompareClick}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                                        <path d="M4 6h7M4 12h7M4 18h7M20 6h-3M20 12h-3M20 18h-3M15 3v18" />
                                    </svg>
                                    {constants.COMPARE || "Compare"} {compareCount > 0 && <span className="tpf-count-bubble">{compareCount}</span>}
                                </button>
                            </GenericTooltip>

                            {(isCompareActive || compareCount > 0) && (
                                <button
                                    type="button"
                                    className="btn tpf-reset-btn-red tpf-mobile-reset-btn"
                                    onClick={onResetClick}
                                    disabled={isProfilesLoading}
                                    title="Reset comparison & clear selection"
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                        <path d="M3 3v5h5" />
                                    </svg>
                                    {constants.RESET || "Reset"}
                                </button>
                            )}

                            <button
                                type="button"
                                className="btn ghost icon-only tpf-eye-btn"
                                onClick={onToggleProfilesClick}
                                aria-label={isProfilesHidden ? (constants.SHOW_PROFILES || "Show profiles") : (constants.HIDE_PROFILES || "Hide profiles")}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                    {isProfilesHidden ? (
                                        <>
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </>
                                    ) : (
                                        <>
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Desktop Right Action Buttons */}
                    <div className={`psf-right desktop-only-filters${isProfilesLoading ? ' tpf-is-loading' : ''}`}>
                        <GenericTooltip
                            tooltipText={compareDisabled ? compareDisabledHint : ""}
                            tooltipPosition="bottom"
                            className="tpf-compare-tooltip-wrapper"
                        >
                            <button
                                className="btn"
                                disabled={compareDisabled || isProfilesLoading}
                                onClick={onCompareClick}
                                style={(compareDisabled || isProfilesLoading) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                    <path d="M4 6h7M4 12h7M4 18h7M20 6h-3M20 12h-3M20 18h-3M15 3v18" />
                                </svg>
                                {constants.COMPARE || "Compare"} {compareCount > 0 && <span className="tpf-count-bubble">{compareCount}</span>}
                            </button>
                        </GenericTooltip>

                        {(isCompareActive || compareCount > 0) ? (
                            <button
                                className="btn tpf-reset-btn-red"
                                onClick={onResetClick}
                                disabled={isProfilesLoading}
                                title="Reset comparison & clear selection"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                    <path d="M3 3v5h5" />
                                </svg>
                                {constants.RESET || "Reset"}
                            </button>
                        ) : (
                            <button className="btn ghost" onClick={onToggleGridView} disabled={isProfilesHidden || isProfilesLoading}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="15" height="15">
                                    {isGridView ? (
                                        <>
                                            <path d="M4 14h6v6M10 14L3 21" />
                                            <path d="M20 10h-6V4M14 10l7-7" />
                                        </>
                                    ) : (
                                        <>
                                            <path d="M15 3h6v6M21 3l-7 7" />
                                            <path d="M9 21H3v-6M3 21l7-7" />
                                        </>
                                    )}
                                </svg>
                                {isGridView ? (constants.SEE_LESS || "See less") : (constants.SEE_ALL || "See all")}
                            </button>
                        )}

                        <button className="btn ghost" onClick={onToggleProfilesClick} disabled={isProfilesLoading}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                {isProfilesHidden ? (
                                    <path d="M6 9l6 6 6-6" />
                                ) : (
                                    <path d="M18 15l-6-6-6 6" />
                                )}
                            </svg>
                            {isProfilesHidden ? (constants.SHOW_PROFILES || "Show profiles") : (constants.HIDE_PROFILES || "Hide profiles")}
                        </button>
                    </div>
                </div>

                {/* Row 2: Candidates count + search — only when profiles panel is visible */}
                {isCandidateProfilesSelected && !isProfilesHidden && (
                    <div className="tp-candidates-header">
                        {!isProfilesLoading && (
                            <span className="tp-candidates-count">
                                CANDIDATES · {searchLower ? visibleCandidatesCount : (totalProfilesCount ?? visibleCandidatesCount)}
                            </span>
                        )}
                        {isProfilesLoading && (
                            <span className="tp-candidates-count tp-candidates-count--loading" aria-label="Loading candidates">
                                <span className="tp-count-shimmer" />
                            </span>
                        )}
                        <div className="tp-candidates-header-actions">
                            <SearchBar
                                value={profileSearchQuery}
                                onChange={onProfileSearchChange}
                                onClear={onProfileSearchClear}
                                placeholder="Search by name"
                                className="tp-candidates-search-bar"
                                disabled={isControlDisabled}
                                infoNote={
                                    profileSearchQuery.trim().length > 0 && profileSearchQuery.trim().length < 3
                                        ? 'Type at least 3 characters to search'
                                        : null
                                }
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Filter Modal — always a centered dialog, rendered via portal above sidebar */}
            <Modal
                open={showMobileFilterModal}
                onClose={() => setShowMobileFilterModal(false)}
                className="tpf-mobile-modal"
            >
                <div className="tpf-modal-handle" />
                <div className="tpf-modal-header">
                    <h3>{constants.FILTERS || "Filters"}</h3>
                    <button
                        type="button"
                        className="tpf-modal-close"
                        onClick={() => setShowMobileFilterModal(false)}
                    >
                        ✕
                    </button>
                </div>

                <div className="tpf-modal-body">
                    {/* CANDIDATE TYPE */}
                    <div className="tpf-modal-section">
                        <label className="tpf-modal-label">{constants.CANDIDATE_TYPE || "CANDIDATE TYPE"}</label>
                        <div className="seg tpf-seg-full">
                            {profileTypes.map(pt => (
                                <button
                                    key={pt.id}
                                    className={draftType === pt.id ? 'on' : ''}
                                    onClick={() => setDraftType(pt.id)}
                                >
                                    {getProfileTypeLabel(pt)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ROLE */}
                    {roles.length > 0 && (
                        <div className="tpf-modal-section">
                            <label className="tpf-modal-label">{constants.ROLE || "ROLE"}</label>
                            <button type="button" className="tpf-select-all" onClick={() => toggleAll(roles, draftRole, setDraftRole)}>
                                <span className={`tpf-select-checkbox${roles.length > 0 && roles.every((role) => (draftRole || []).includes(role)) ? ' is-checked' : ''}`} aria-hidden="true" />
                                {roles.length > 0 && roles.every((role) => (draftRole || []).includes(role)) ? 'Unselect all' : 'Select all'}
                            </button>
                            <div className="tpf-pills-grid">
                                {roles.map(r => (
                                    <button
                                        key={r}
                                        type="button"
                                        className={`tpf-pill ${(Array.isArray(draftRole) ? draftRole : []).includes(r) ? 'is-selected' : ''}`}
                                        onClick={() => setDraftRole((prev) => (prev || []).includes(r) ? prev.filter((v) => v !== r) : [...(prev || []), r])}
                                    >
                                        <span className={`tpf-pill-checkbox${(Array.isArray(draftRole) ? draftRole : []).includes(r) ? ' is-checked' : ''}`} aria-hidden="true" />
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* COMPANY */}
                    {companies.length > 0 && (
                        <div className="tpf-modal-section">
                            <label className="tpf-modal-label">{constants.COMPANY || "COMPANY"}</label>
                            <button type="button" className="tpf-select-all" onClick={() => toggleAll(companies, draftCompany, setDraftCompany)}>
                                <span className={`tpf-select-checkbox${companies.length > 0 && companies.every((company) => (draftCompany || []).includes(company)) ? ' is-checked' : ''}`} aria-hidden="true" />
                                {companies.length > 0 && companies.every((company) => (draftCompany || []).includes(company)) ? 'Unselect all' : 'Select all'}
                            </button>
                            <div className="tpf-pills-grid">
                                {companies.map(c => (
                                    <button
                                        key={c}
                                        type="button"
                                        className={`tpf-pill ${(Array.isArray(draftCompany) ? draftCompany : []).includes(c) ? 'is-selected' : ''}`}
                                        onClick={() => setDraftCompany((prev) => (prev || []).includes(c) ? prev.filter((v) => v !== c) : [...(prev || []), c])}
                                    >
                                        <span className={`tpf-pill-checkbox${(Array.isArray(draftCompany) ? draftCompany : []).includes(c) ? ' is-checked' : ''}`} aria-hidden="true" />
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}




                    {states.length > 0 && (
                        <div className="tpf-modal-section">
                            <label className="tpf-modal-label">{constants.STATE || "STATE"}</label>
                            <button type="button" className="tpf-select-all" onClick={toggleAllDraftStates}>
                                <span className={`tpf-select-checkbox${states.length > 0 && states.every((state) => (draftState || []).includes(state)) ? ' is-checked' : ''}`} aria-hidden="true" />
                                {states.length > 0 && states.every((state) => (draftState || []).includes(state)) ? 'Unselect all' : 'Select all'}
                            </button>
                            <div className="tpf-pills-grid">
                                {states.map(s => (
                                    <button
                                        key={s}
                                        type="button"
                                        className={`tpf-pill ${(Array.isArray(draftState) ? draftState : []).includes(s) ? 'is-selected' : ''}`}
                                        onClick={() => toggleDraftState(s)}
                                    >
                                        <span className={`tpf-pill-checkbox${(Array.isArray(draftState) ? draftState : []).includes(s) ? ' is-checked' : ''}`} aria-hidden="true" />
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TERRITORY */}
                    {draftTerritories.length > 0 && (
                        <div className="tpf-modal-section">
                            <label className="tpf-modal-label">{constants.TERRITORY || "TERRITORY"}</label>
                            <button type="button" className="tpf-select-all" onClick={() => toggleAll(draftTerritories, draftTerritory, setDraftTerritory)}>
                                <span className={`tpf-select-checkbox${draftTerritories.length > 0 && draftTerritories.every((territory) => (draftTerritory || []).includes(territory)) ? ' is-checked' : ''}`} aria-hidden="true" />
                                {draftTerritories.length > 0 && draftTerritories.every((territory) => (draftTerritory || []).includes(territory)) ? 'Unselect all' : 'Select all'}
                            </button>
                            <div className="tpf-pills-grid">
                                {draftTerritories.map(t => (
                                    <button
                                        key={t}
                                        type="button"
                                        className={`tpf-pill ${(Array.isArray(draftTerritory) ? draftTerritory : []).includes(t) ? 'is-selected' : ''}`}
                                        onClick={() => setDraftTerritory((prev) => (prev || []).includes(t) ? prev.filter((v) => v !== t) : [...(prev || []), t])}
                                    >
                                        <span className={`tpf-pill-checkbox${(Array.isArray(draftTerritory) ? draftTerritory : []).includes(t) ? ' is-checked' : ''}`} aria-hidden="true" />
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* SORT BY */}
                    <div className="tpf-modal-section">
                        <label className="tpf-modal-label">{constants.SORT_BY || "SORT BY"}</label>
                        <div className="seg tpf-seg-full">
                            <button
                                className={draftSort === 'latest' ? 'on' : ''}
                                onClick={() => setDraftSort('latest')}
                            >
                                {constants.LATEST || "Latest"}
                            </button>
                            <button
                                className={draftSort === 'az' ? 'on' : ''}
                                onClick={() => setDraftSort('az')}
                            >
                                A–Z
                            </button>
                            <button
                                className={draftSort === 'za' ? 'on' : ''}
                                onClick={() => setDraftSort('za')}
                            >
                                Z–A
                            </button>
                        </div>
                    </div>
                </div>

                {/* Modal Footer: Close + Apply */}
                <div className="tpf-modal-footer">
                    <button
                        type="button"
                        className="tpf-clear-btn"
                        onClick={() => {
                            setShowMobileFilterModal(false);
                        }}
                    >
                        {constants.CLOSE || "Close"}
                    </button>
                    <button
                        type="button"
                        className="tpf-apply-btn"
                        onClick={handleApplyMobileFilters}
                    >
                        {constants.APPLY || "Apply"}
                    </button>
                </div>

            </Modal>
        </>
    );
};

export default TalentPulseFilterBar;
